/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { Loader } from '@googlemaps/js-api-loader';

export class MapsService {
  private static instance: MapsService;
  public map: any = null;
  public google: any = null;
  public placesService: any = null;
  public geocoder: any = null;
  public directionsService: any = null;
  public directionsRenderer: any = null;
  public apiKey: string = '';
  private markers: any[] = [];

  private constructor() {}

  public static getInstance(): MapsService {
    if (!MapsService.instance) {
      MapsService.instance = new MapsService();
    }
    return MapsService.instance;
  }

  async init(container: HTMLElement, apiKey: string) {
    this.apiKey = apiKey;
    const loader = new Loader({
      apiKey: apiKey,
      version: 'weekly',
      libraries: ['places', 'geometry']
    });

    this.google = await loader.load();
    this.map = new this.google.maps.Map(container, {
      center: { lat: -23.5505, lng: -46.6333 },
      zoom: 12,
    });
    
    this.placesService = new this.google.maps.places.PlacesService(this.map);
    this.geocoder = new this.google.maps.Geocoder();
    this.directionsService = new this.google.maps.DirectionsService();
    this.directionsRenderer = new this.google.maps.DirectionsRenderer({ map: this.map });
    
    return { map: this.map, google: this.google };
  }

  private drawingRectangle: any = null;
  private isSelecting: boolean = false;
  private startLatLng: any = null;
  private selectionListeners: any[] = [];

  public startRectangleSelection(onComplete: (bounds: any) => void) {
    this.isSelecting = true;
    this.map.setOptions({ draggable: false, scrollwheel: false, disableDoubleClickZoom: true });
    
    const mouseDownListener = this.map.addListener('mousedown', (e: any) => {
      this.startLatLng = e.latLng;
      if (this.drawingRectangle) this.drawingRectangle.setMap(null);
      
      this.drawingRectangle = new this.google.maps.Rectangle({
        map: this.map,
        bounds: {
          north: this.startLatLng.lat(),
          south: this.startLatLng.lat(),
          east: this.startLatLng.lng(),
          west: this.startLatLng.lng()
        },
        fillColor: '#1a73e8',
        fillOpacity: 0.15,
        strokeColor: '#1a73e8',
        strokeWeight: 2,
        clickable: false
      });
    });

    const mouseMoveListener = this.map.addListener('mousemove', (e: any) => {
      if (!this.drawingRectangle) return;
      
      const bounds = {
        north: Math.max(this.startLatLng.lat(), e.latLng.lat()),
        south: Math.min(this.startLatLng.lat(), e.latLng.lat()),
        east: Math.max(this.startLatLng.lng(), e.latLng.lng()),
        west: Math.min(this.startLatLng.lng(), e.latLng.lng())
      };
      
      this.drawingRectangle.setBounds(bounds);
    });

    const mouseUpListener = this.map.addListener('mouseup', () => {
      if (!this.drawingRectangle) return;
      
      const bounds = this.drawingRectangle.getBounds();
      this.stopRectangleSelection();
      onComplete(bounds);
    });

    this.selectionListeners = [mouseDownListener, mouseMoveListener, mouseUpListener];
  }

  public stopRectangleSelection() {
    this.isSelecting = false;
    this.map.setOptions({ draggable: true, scrollwheel: true, disableDoubleClickZoom: false });
    this.selectionListeners.forEach(l => this.google.maps.event.removeListener(l));
    this.selectionListeners = [];
    this.startLatLng = null;
  }

  public clearSelection() {
    if (this.drawingRectangle) {
      this.drawingRectangle.setMap(null);
      this.drawingRectangle = null;
    }
  }

  async getDetails(placeId: string, fields: string[]): Promise<any> {
    let retries = 0;
    const maxRetries = 3;
    
    const attempt = (): Promise<any> => {
      return new Promise((resolve, reject) => {
        this.placesService.getDetails({ placeId, fields }, (place: any, status: any) => {
          if (status === this.google.maps.places.PlacesServiceStatus.OK) {
            resolve(place);
          } else if (status === this.google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT && retries < maxRetries) {
            retries++;
            const delay = Math.pow(2, retries) * 1000;
            console.warn(`Quota limit hit, retrying in ${delay / 1000}s...`);
            setTimeout(() => resolve(attempt()), delay);
          } else {
            console.error('getDetails failed:', status);
            reject(status);
          }
        });
      });
    };

    return attempt();
  }

  textSearch(query: string, location?: any, radius?: number, retryCount = 0): Promise<{results: any[], pagination: any}> {
    return new Promise((resolve) => {
      // Prioritize Brazilian results
      const request: any = { 
        query,
        region: 'BR'
      };
      if (location) request.location = location;
      if (radius) request.radius = radius;

      this.placesService.textSearch(request, (results: any, status: any, pagination: any) => {
        if (status === this.google.maps.places.PlacesServiceStatus.OK && results) {
          resolve({ results, pagination });
        } else if (status === this.google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT && retryCount < 3) {
          const delay = (retryCount + 1) * 600;
          console.warn(`textSearch limit hit, retrying in ${delay}ms...`);
          setTimeout(() => {
            this.textSearch(query, location, radius, retryCount + 1).then(resolve);
          }, delay);
        } else if (status === this.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          // Unico caso em que "vazio" e uma resposta legitima: a busca rodou e
          // nao havia nada na area. Silencioso de proposito.
          resolve({ results: [], pagination: null });
        } else {
          // Antes TODOS os status caiam aqui devolvendo lista vazia, sem log.
          // Resultado: falha de API ficava indistinguivel de "nao achei nada", a
          // varredura seguia para a proxima etapa e a campanha consumia os passos
          // sem capturar um lead sequer — exatamente o que aconteceu no teste local.
          console.error(
            `[maps-service] textSearch FALHOU. status=${status} query="${query}"` +
              '\n' + MapsService.explicarStatus(status),
          );
          resolve({ results: [], pagination: null, status } as any);
        }
      });
    });
  }

  /** Traduz o status do PlacesService para algo acionavel no console. */
  private static explicarStatus(status: string): string {
    switch (status) {
      case 'REQUEST_DENIED':
        return [
          '  O Google recusou a requisicao. Causas, em ordem de frequencia:',
          '   1. Faturamento desativado no projeto do Google Cloud',
          '      -> https://console.cloud.google.com/project/_/billing/enable',
          '   2. "Places API" nao habilitada no projeto',
          '   3. A chave tem restricao de API que nao inclui Places',
          '   4. Restricao de referrer nao cobre ' + window.location.origin,
        ].join('\n');
      case 'OVER_QUERY_LIMIT':
        return '  Cota estourada mesmo apos 3 tentativas com backoff. Aguarde ou aumente a cota.';
      case 'INVALID_REQUEST':
        return '  Requisicao malformada — normalmente query vazia ou radius fora do limite.';
      case 'NOT_FOUND':
        return '  Referencia de lugar inexistente.';
      default:
        return '  Status nao mapeado. Veja PlacesServiceStatus na documentacao do Maps.';
    }
  }

  addMarker(options: any) {
    const marker = new this.google.maps.Marker({
      ...options,
      map: this.map
    });
    this.markers.push(marker);
    return marker;
  }

  clearAllMarkers() {
    for (const marker of this.markers) {
      marker.setMap(null);
    }
    this.markers = [];
  }
}
